import type { ParsedEpochPower } from "../dataSources/epochPower";
import type { ComputeDeploymentSliceResult } from "./computeDeployment";

export type PowerEstimateKind = "epoch-observed-site-snapshot" | "physical-estimate" | "projection";
export type PowerBacktestModelId = "lag-1" | "seasonal-lag-4" | "chip-delivery-stock-flow";

export interface PowerEstimate {
  value: number;
  low: number;
  high: number;
  unit: "GW" | "TWh";
  kind: PowerEstimateKind;
}

export interface PowerScenario {
  deploymentLagQuarters: number;
  utilization: number;
  pue: number;
}

export interface PowerForecastPoint {
  period: string;
  horizon: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;
  itNameplateGw: PowerEstimate;
  itLoadGw: PowerEstimate;
  facilityLoadGw: PowerEstimate;
  quarterlyTwh: PowerEstimate;
  annualizedTwh: PowerEstimate;
  intervalSource: "fixed-seed-physical-input-monte-carlo";
  shipmentInput: "upstream-forecast" | "post-4q-extrapolation";
}

export interface PowerBacktestPoint {
  period: string;
  actual: number;
  predicted: number;
  error: number;
}

export interface PowerBacktestMetrics {
  observations: number;
  mae: number;
  rmse: number;
  mape: number;
  intervalObservations: number;
  intervalCoverage90Pct: number | null;
}

export interface PowerBacktestCandidate {
  id: PowerBacktestModelId;
  label: string;
  full: PowerBacktestMetrics;
  recent8: PowerBacktestMetrics;
  points: PowerBacktestPoint[];
}

export interface PowerDemandOptions {
  seed?: number;
  monteCarloSamples?: number;
  scenario?: Partial<PowerScenario>;
}

export interface PowerDemandSliceResult {
  monitor: {
    period: string;
    asOf: string;
    acceleratorNameplateGw: PowerEstimate;
    itOverheadGw: PowerEstimate;
    itNameplateGw: PowerEstimate;
    itLoadGw: PowerEstimate;
    facilityLoadGw: PowerEstimate;
    quarterlyTwh: PowerEstimate;
    annualizedTwh: PowerEstimate;
    epochCoverage: {
      kind: "epoch-observed-site-snapshot";
      coveredSites: number;
      observedItNameplateGw: number;
      observedFacilityNameplateGw: number;
      commonWatermark: string;
      sourceLatestObservedDate: string;
      sourceCoverageWarning: string;
    };
    quality: {
      computeCoveragePct: number;
      epochTimelineRows: number;
      epochProjectedRowsExcluded: number;
      frontierRowsUsedAsGlobalSum: 0;
    };
  };
  forecast: {
    originPeriod: string;
    status: "research-baseline";
    path: PowerForecastPoint[];
    baselinePath: PowerForecastPoint[];
    horizons: {
      quarter1: PowerForecastPoint;
      quarter4: PowerForecastPoint;
      quarter8: PowerForecastPoint;
    };
    scenario: PowerScenario;
    baselineScenario: PowerScenario;
    scenarioImpact: {
      active: boolean;
      historicalBacktestLocked: true;
      quarter4FacilityLoadDeltaGw: number;
      quarter8FacilityLoadDeltaGw: number;
      directChannels: string[];
    };
    constraints: {
      observedSingleSiteItCeilingGw: number;
      projectedSingleSiteItCeilingGw: number | null;
      medianFrontierRecordIntervalDays: number | null;
      futureFrontierDates: string[];
      use: "single-site-scale-and-commissioning-cadence-only";
    };
  };
  backtest: {
    target: "epoch-covered-site-it-nameplate-gw";
    selectedBenchmark: PowerBacktestModelId;
    status: "research-baseline";
    statusReason: string;
    models: Record<PowerBacktestModelId, PowerBacktestCandidate>;
  };
  methodology: {
    productionEligible: false;
    physicalPath: string;
    overhead: string;
    energyConversion: string;
    uncertainty: string;
    backtest: string;
    scenarioPolicy: string;
    frontierPolicy: string;
  };
  sources: Array<{
    id: string;
    label: string;
    url: string;
    role: string;
    asOf: string;
  }>;
}

interface ShipmentPoint {
  period: string;
  p05: number;
  p50: number;
  p95: number;
  input: "historical" | "upstream-forecast" | "post-4q-extrapolation";
}

interface DrawResult {
  acceleratorGw: number;
  itNameplateGw: number;
  itLoadGw: number;
  facilityLoadGw: number;
  quarterlyTwh: number;
  annualizedTwh: number;
}

const BASELINE_SCENARIO: PowerScenario = {
  deploymentLagQuarters: 1,
  utilization: 0.70,
  pue: 1.25,
};
const IT_OVERHEAD_FACTOR = 1.14;
const ANNUAL_SURVIVAL = 0.94;

function periodIndex(period: string) {
  const match = /^(\d{4})-Q([1-4])$/.exec(period);
  if (!match) throw new Error(`Power demand period must use YYYY-Qn: ${period}.`);
  return Number(match[1]) * 4 + Number(match[2]) - 1;
}

function periodFromIndex(index: number) {
  return `${Math.floor(index / 4)}-Q${(index % 4) + 1}`;
}

function nextQuarter(period: string, offset: number) {
  return periodFromIndex(periodIndex(period) + offset);
}

function quarterEnd(period: string) {
  const match = /^(\d{4})-Q([1-4])$/.exec(period)!;
  const year = Number(match[1]);
  const quarter = Number(match[2]);
  return new Date(Date.UTC(year, quarter * 3, 0)).toISOString().slice(0, 10);
}

function hoursInQuarter(period: string) {
  const match = /^(\d{4})-Q([1-4])$/.exec(period)!;
  const year = Number(match[1]);
  const quarter = Number(match[2]);
  return (Date.UTC(year, quarter * 3, 1) - Date.UTC(year, (quarter - 1) * 3, 1)) / 3_600_000;
}

function seededRandom(seed: number) {
  let state = seed >>> 0 || 0x9e3779b9;
  return () => {
    state ^= state << 13;
    state ^= state >>> 17;
    state ^= state << 5;
    return (state >>> 0) / 4_294_967_296;
  };
}

function hashText(value: string) {
  let hash = 2_166_136_261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16_777_619);
  }
  return hash >>> 0;
}

function percentile(values: number[], probability: number) {
  if (!values.length) throw new Error("Power-demand percentile requires a non-empty sample.");
  const sorted = [...values].sort((left, right) => left - right);
  const position = probability * (sorted.length - 1);
  const lower = Math.floor(position);
  const upper = Math.ceil(position);
  return sorted[lower] + (sorted[upper] - sorted[lower]) * (position - lower);
}

function sampleThreePoint(point: ShipmentPoint, probability: number) {
  if (probability <= 0.05) return point.p05;
  if (probability <= 0.5) return point.p05 + ((point.p50 - point.p05) * (probability - 0.05)) / 0.45;
  if (probability <= 0.95) return point.p50 + ((point.p95 - point.p50) * (probability - 0.5)) / 0.45;
  return point.p95;
}

function clamp(value: number, low: number, high: number) {
  return Math.max(low, Math.min(high, value));
}

function validateScenario(scenario: PowerScenario) {
  if (!Number.isInteger(scenario.deploymentLagQuarters) || scenario.deploymentLagQuarters < 0 || scenario.deploymentLagQuarters > 4) {
    throw new Error("deploymentLagQuarters must be an integer from 0 through 4.");
  }
  if (!Number.isFinite(scenario.utilization) || scenario.utilization <= 0 || scenario.utilization > 1) {
    throw new Error("utilization must be greater than 0 and no greater than 1.");
  }
  if (!Number.isFinite(scenario.pue) || scenario.pue < 1 || scenario.pue > 2.5) {
    throw new Error("PUE must be at least 1 and no greater than 2.5.");
  }
}

function shipmentSeries(compute: ComputeDeploymentSliceResult): ShipmentPoint[] {
  const metric = compute.monitor.metrics.find((row) => row.id === "new_it_nameplate_mw");
  if (!metric || metric.value <= 0) throw new Error("Compute input is missing a positive new-IT-nameplate monitor metric.");
  const backtest = compute.backtest.metrics.new_it_nameplate_mw;
  if (!backtest?.selectedPoints.length) throw new Error("Compute input is missing historical new-IT-nameplate observations.");
  const historical = backtest.selectedPoints.map((point): ShipmentPoint => ({
    period: point.period,
    p05: point.period === compute.monitor.period ? metric.low : point.actual * 0.9,
    p50: point.actual,
    p95: point.period === compute.monitor.period ? metric.high : point.actual * 1.1,
    input: "historical",
  }));
  for (let index = 1; index < historical.length; index += 1) {
    if (periodIndex(historical[index].period) !== periodIndex(historical[index - 1].period) + 1) {
      throw new Error(`Compute nameplate shipment series has a calendar gap at ${historical[index].period}.`);
    }
  }
  if (historical.at(-1)!.period !== compute.monitor.period) {
    throw new Error("Compute monitor and historical nameplate series do not share the same watermark.");
  }
  const upstream = compute.forecast.paths.new_it_nameplate_mw;
  if (upstream.length !== 4 || upstream.some((point, index) => point.period !== nextQuarter(compute.monitor.period, index + 1))) {
    throw new Error("Compute nameplate forecast must provide contiguous 1Q through 4Q points.");
  }
  const future = upstream.map((point): ShipmentPoint => ({
    period: point.period,
    p05: point.low,
    p50: point.predicted,
    p95: point.high,
    input: "upstream-forecast",
  }));
  const fourth = future.at(-1)!;
  const quarterlyDrift = Math.max(0, (fourth.p50 - metric.value) / 4);
  const lowRatio = fourth.p50 === 0 ? 1 : fourth.p05 / fourth.p50;
  const highRatio = fourth.p50 === 0 ? 1 : fourth.p95 / fourth.p50;
  for (let horizon = 5; horizon <= 8; horizon += 1) {
    const p50 = Math.max(0, fourth.p50 + quarterlyDrift * (horizon - 4));
    const widening = 1 + (horizon - 4) * 0.08;
    future.push({
      period: nextQuarter(compute.monitor.period, horizon),
      p05: Math.max(0, p50 * lowRatio / widening),
      p50,
      p95: p50 * highRatio * widening,
      input: "post-4q-extrapolation",
    });
  }
  return [...historical, ...future];
}

function stockMw(
  shipments: ShipmentPoint[],
  targetPeriod: string,
  lag: number,
  annualSurvival: number,
  valueFor: (point: ShipmentPoint) => number,
) {
  const target = periodIndex(targetPeriod);
  const quarterlySurvival = annualSurvival ** 0.25;
  return shipments.reduce((sum, shipment) => {
    const age = target - periodIndex(shipment.period);
    if (age < lag) return sum;
    return sum + valueFor(shipment) * quarterlySurvival ** age;
  }, 0);
}

function physicalPoint(
  shipments: ShipmentPoint[],
  period: string,
  scenario: PowerScenario,
): DrawResult {
  const acceleratorGw = stockMw(shipments, period, scenario.deploymentLagQuarters, ANNUAL_SURVIVAL, (point) => point.p50) / 1_000;
  const itNameplateGw = acceleratorGw * IT_OVERHEAD_FACTOR;
  const itLoadGw = itNameplateGw * scenario.utilization;
  const facilityLoadGw = itLoadGw * scenario.pue;
  const quarterlyTwh = facilityLoadGw * hoursInQuarter(period) / 1_000;
  return {
    acceleratorGw,
    itNameplateGw,
    itLoadGw,
    facilityLoadGw,
    quarterlyTwh,
    annualizedTwh: facilityLoadGw * 8.76,
  };
}

function monteCarlo(
  shipments: ShipmentPoint[],
  period: string,
  scenario: PowerScenario,
  seed: number,
  samples: number,
) {
  const random = seededRandom(seed);
  const draws: DrawResult[] = [];
  for (let sample = 0; sample < samples; sample += 1) {
    const uncertainLag = scenario.deploymentLagQuarters + (random() < 0.5 ? 0 : 1);
    const annualSurvival = 0.90 + random() * 0.08;
    const acceleratorGw = stockMw(shipments, period, uncertainLag, annualSurvival, (point) => sampleThreePoint(point, random())) / 1_000;
    const overheadFactor = 1.10 + random() * 0.08;
    const utilization = clamp(scenario.utilization + (random() - 0.5) * 0.16, 0.05, 1);
    const pue = clamp(scenario.pue + (random() - 0.5) * 0.16, 1, 2.5);
    const itNameplateGw = acceleratorGw * overheadFactor;
    const itLoadGw = itNameplateGw * utilization;
    const facilityLoadGw = itLoadGw * pue;
    draws.push({
      acceleratorGw,
      itNameplateGw,
      itLoadGw,
      facilityLoadGw,
      quarterlyTwh: facilityLoadGw * hoursInQuarter(period) / 1_000,
      annualizedTwh: facilityLoadGw * 8.76,
    });
  }
  return draws;
}

function estimate(value: number, draws: number[], unit: "GW" | "TWh", kind: PowerEstimateKind): PowerEstimate {
  return {
    value,
    low: Math.min(value, percentile(draws, 0.05)),
    high: Math.max(value, percentile(draws, 0.95)),
    unit,
    kind,
  };
}

function toForecastPoint(
  shipments: ShipmentPoint[],
  origin: string,
  horizon: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8,
  scenario: PowerScenario,
  seed: number,
  samples: number,
): PowerForecastPoint {
  const period = nextQuarter(origin, horizon);
  const point = physicalPoint(shipments, period, scenario);
  const draws = monteCarlo(shipments, period, scenario, seed ^ hashText(period), samples);
  return {
    period,
    horizon,
    itNameplateGw: estimate(point.itNameplateGw, draws.map((draw) => draw.itNameplateGw), "GW", "projection"),
    itLoadGw: estimate(point.itLoadGw, draws.map((draw) => draw.itLoadGw), "GW", "projection"),
    facilityLoadGw: estimate(point.facilityLoadGw, draws.map((draw) => draw.facilityLoadGw), "GW", "projection"),
    quarterlyTwh: estimate(point.quarterlyTwh, draws.map((draw) => draw.quarterlyTwh), "TWh", "projection"),
    annualizedTwh: estimate(point.annualizedTwh, draws.map((draw) => draw.annualizedTwh), "TWh", "projection"),
    intervalSource: "fixed-seed-physical-input-monte-carlo",
    shipmentInput: horizon <= 4 ? "upstream-forecast" : "post-4q-extrapolation",
  };
}

function epochSnapshot(epoch: ParsedEpochPower, period: string) {
  const end = quarterEnd(period);
  const latest = new Map<string, ParsedEpochPower["actual"][number]>();
  for (const row of epoch.actual) {
    if (row.date > end) continue;
    const current = latest.get(row.site);
    if (!current || row.date > current.date) latest.set(row.site, row);
  }
  const rows = [...latest.values()].filter((row) => row.itNameplateMw > 0);
  return {
    sites: rows.length,
    itGw: rows.reduce((sum, row) => sum + row.itNameplateMw, 0) / 1_000,
    facilityGw: rows.reduce((sum, row) => sum + row.facilityNameplateMw, 0) / 1_000,
  };
}

function epochQuarterlySeries(epoch: ParsedEpochPower) {
  const positive = epoch.actual.filter((row) => row.itNameplateMw > 0);
  if (!positive.length) throw new Error("Power-demand backtest needs positive Epoch site observations.");
  const first = Math.min(...positive.map((row) => periodIndex(row.period)));
  const last = Math.max(...positive.map((row) => periodIndex(row.period)));
  return Array.from({ length: last - first + 1 }, (_, offset) => {
    const period = periodFromIndex(first + offset);
    return { period, actual: epochSnapshot(epoch, period).itGw };
  }).filter((row) => row.actual > 0);
}

function backtestMetrics(points: PowerBacktestPoint[]): PowerBacktestMetrics {
  if (!points.length) {
    return { observations: 0, mae: Number.NaN, rmse: Number.NaN, mape: Number.NaN, intervalObservations: 0, intervalCoverage90Pct: null };
  }
  const absolute = points.map((point) => Math.abs(point.error));
  let intervalObservations = 0;
  let intervalHits = 0;
  // A nominal 90% sequential residual interval requires at least nine genuine
  // prior out-of-sample errors. Earlier points remain accuracy observations but
  // are not silently counted as interval validations.
  for (let index = 9; index < points.length; index += 1) {
    const radius = percentile(absolute.slice(0, index), 0.9);
    intervalObservations += 1;
    if (absolute[index] <= radius) intervalHits += 1;
  }
  return {
    observations: points.length,
    mae: absolute.reduce((sum, value) => sum + value, 0) / points.length,
    rmse: Math.sqrt(points.reduce((sum, point) => sum + point.error ** 2, 0) / points.length),
    mape: points.reduce((sum, point) => sum + Math.abs(point.error / point.actual), 0) / points.length * 100,
    intervalObservations,
    intervalCoverage90Pct: intervalObservations ? intervalHits / intervalObservations * 100 : null,
  };
}

function runBacktest(epoch: ParsedEpochPower, shipments: ShipmentPoint[]) {
  const actual = epochQuarterlySeries(epoch);
  const candidates: Record<PowerBacktestModelId, PowerBacktestPoint[]> = {
    "lag-1": [],
    "seasonal-lag-4": [],
    "chip-delivery-stock-flow": [],
  };
  for (let index = 4; index < actual.length; index += 1) {
    const target = actual[index];
    const origin = actual[index - 1];
    const originStock = stockMw(shipments, origin.period, BASELINE_SCENARIO.deploymentLagQuarters, ANNUAL_SURVIVAL, (row) => row.p50);
    const targetStock = stockMw(shipments, target.period, BASELINE_SCENARIO.deploymentLagQuarters, ANNUAL_SURVIVAL, (row) => row.p50);
    if (originStock <= 0 || targetStock <= 0) continue;
    const predictions: Record<PowerBacktestModelId, number> = {
      "lag-1": origin.actual,
      "seasonal-lag-4": actual[index - 4].actual,
      "chip-delivery-stock-flow": origin.actual * targetStock / originStock,
    };
    for (const id of Object.keys(predictions) as PowerBacktestModelId[]) {
      candidates[id].push({
        period: target.period,
        actual: target.actual,
        predicted: predictions[id],
        error: predictions[id] - target.actual,
      });
    }
  }
  const labels: Record<PowerBacktestModelId, string> = {
    "lag-1": "Lag-1",
    "seasonal-lag-4": "Seasonal lag-4",
    "chip-delivery-stock-flow": "Chip delivery stock-flow",
  };
  const models = Object.fromEntries((Object.keys(candidates) as PowerBacktestModelId[]).map((id) => {
    const points = candidates[id];
    return [id, {
      id,
      label: labels[id],
      full: backtestMetrics(points),
      recent8: backtestMetrics(points.slice(-8)),
      points,
    } satisfies PowerBacktestCandidate];
  })) as Record<PowerBacktestModelId, PowerBacktestCandidate>;
  const selected = (Object.values(models).filter((model) => model.full.observations > 0)
    .sort((left, right) => left.full.rmse - right.full.rmse)[0]?.id ?? "lag-1") as PowerBacktestModelId;
  return { models, selected };
}

/** Deep, deterministic seam for the power monitor, physical forecast and OOS diagnostics. */
export function runPowerDemandSlice(
  input: { compute: ComputeDeploymentSliceResult; epochPower: ParsedEpochPower },
  options: PowerDemandOptions = {},
): PowerDemandSliceResult {
  const samples = options.monteCarloSamples ?? 2_048;
  if (!Number.isInteger(samples) || samples < 500) throw new Error("monteCarloSamples must be an integer of at least 500.");
  const seed = options.seed ?? 20_260_713;
  const scenario: PowerScenario = { ...BASELINE_SCENARIO, ...options.scenario };
  validateScenario(scenario);
  const shipments = shipmentSeries(input.compute);
  const origin = input.compute.monitor.period;

  const monitorPoint = physicalPoint(shipments, origin, BASELINE_SCENARIO);
  const monitorDraws = monteCarlo(shipments, origin, BASELINE_SCENARIO, seed ^ hashText("monitor"), samples);
  const accelerator = estimate(monitorPoint.acceleratorGw, monitorDraws.map((draw) => draw.acceleratorGw), "GW", "physical-estimate");
  const nameplate = estimate(monitorPoint.itNameplateGw, monitorDraws.map((draw) => draw.itNameplateGw), "GW", "physical-estimate");
  const overhead = estimate(
    monitorPoint.itNameplateGw - monitorPoint.acceleratorGw,
    monitorDraws.map((draw) => draw.itNameplateGw - draw.acceleratorGw),
    "GW",
    "physical-estimate",
  );
  const epochAtOrigin = epochSnapshot(input.epochPower, origin);
  const latestEpochPeriod = input.epochPower.quality.latestActualDate.slice(0, 7);
  const sourceLatestQuarter = `${latestEpochPeriod.slice(0, 4)}-Q${Math.ceil(Number(latestEpochPeriod.slice(5, 7)) / 3)}`;

  const futureSeed = seed ^ hashText("future");
  const baselinePath = ([1, 2, 3, 4, 5, 6, 7, 8] as const).map((horizon) =>
    toForecastPoint(shipments, origin, horizon, BASELINE_SCENARIO, futureSeed, samples));
  const path = ([1, 2, 3, 4, 5, 6, 7, 8] as const).map((horizon) =>
    toForecastPoint(shipments, origin, horizon, scenario, futureSeed, samples));
  const historicalBacktest = runBacktest(input.epochPower, shipments.filter((row) => row.input === "historical"));
  const computeMetric = input.compute.monitor.metrics.find((row) => row.id === "new_it_nameplate_mw")!;

  return {
    monitor: {
      period: origin,
      asOf: input.epochPower.quality.asOf,
      acceleratorNameplateGw: accelerator,
      itOverheadGw: overhead,
      itNameplateGw: nameplate,
      itLoadGw: estimate(monitorPoint.itLoadGw, monitorDraws.map((draw) => draw.itLoadGw), "GW", "physical-estimate"),
      facilityLoadGw: estimate(monitorPoint.facilityLoadGw, monitorDraws.map((draw) => draw.facilityLoadGw), "GW", "physical-estimate"),
      quarterlyTwh: estimate(monitorPoint.quarterlyTwh, monitorDraws.map((draw) => draw.quarterlyTwh), "TWh", "physical-estimate"),
      annualizedTwh: estimate(monitorPoint.annualizedTwh, monitorDraws.map((draw) => draw.annualizedTwh), "TWh", "physical-estimate"),
      epochCoverage: {
        kind: "epoch-observed-site-snapshot",
        coveredSites: epochAtOrigin.sites,
        observedItNameplateGw: epochAtOrigin.itGw,
        observedFacilityNameplateGw: epochAtOrigin.facilityGw,
        commonWatermark: sourceLatestQuarter < origin ? sourceLatestQuarter : origin,
        sourceLatestObservedDate: input.epochPower.quality.latestActualDate,
        sourceCoverageWarning: "Epoch tracks selected large sites rather than the global installed base; the site snapshot is a calibration cross-check, not a global sum.",
      },
      quality: {
        computeCoveragePct: computeMetric.coveragePct,
        epochTimelineRows: input.epochPower.quality.totalTimelineRows,
        epochProjectedRowsExcluded: input.epochPower.quality.projectedRows,
        frontierRowsUsedAsGlobalSum: 0,
      },
    },
    forecast: {
      originPeriod: origin,
      status: "research-baseline",
      path,
      baselinePath,
      horizons: { quarter1: path[0], quarter4: path[3], quarter8: path[7] },
      scenario,
      baselineScenario: BASELINE_SCENARIO,
      scenarioImpact: {
        active: JSON.stringify(scenario) !== JSON.stringify(BASELINE_SCENARIO),
        historicalBacktestLocked: true,
        quarter4FacilityLoadDeltaGw: path[3].facilityLoadGw.value - baselinePath[3].facilityLoadGw.value,
        quarter8FacilityLoadDeltaGw: path[7].facilityLoadGw.value - baselinePath[7].facilityLoadGw.value,
        directChannels: [
          "deployment lag → deployed IT nameplate",
          "utilization → estimated IT load",
          "PUE → facility load and TWh",
        ],
      },
      constraints: {
        observedSingleSiteItCeilingGw: input.epochPower.constraints.currentSingleSiteItNameplateCeilingMw / 1_000,
        projectedSingleSiteItCeilingGw: input.epochPower.constraints.projectedSingleSiteItNameplateCeilingMw == null
          ? null
          : input.epochPower.constraints.projectedSingleSiteItNameplateCeilingMw / 1_000,
        medianFrontierRecordIntervalDays: input.epochPower.constraints.medianObservedFrontierRecordIntervalDays,
        futureFrontierDates: input.epochPower.constraints.futureFrontierDates,
        use: "single-site-scale-and-commissioning-cadence-only",
      },
    },
    backtest: {
      target: "epoch-covered-site-it-nameplate-gw",
      selectedBenchmark: historicalBacktest.selected,
      status: "research-baseline",
      statusReason: "The current Epoch snapshot is not a point-in-time vintage panel and covers selected large sites; even adequate OOS counts cannot promote this slice to production.",
      models: historicalBacktest.models,
    },
    methodology: {
      productionEligible: false,
      physicalPath: "Quarterly accelerator nameplate deliveries are deployed after a lag, accumulated with survival, and converted into IT nameplate stock.",
      overhead: `Accelerator stock is multiplied by a separately disclosed network/CPU/storage overhead factor of ${IT_OVERHEAD_FACTOR.toFixed(2)}×.`,
      energyConversion: "Estimated IT load = IT nameplate × utilization; facility load = IT load × PUE; energy = facility GW × exact quarter hours.",
      uncertainty: `Fixed-seed ${samples}-draw Monte Carlo propagates shipment quantiles, a one-quarter deployment-lag range, survival, utilization, overhead and PUE. Seed=${seed}.`,
      backtest: "Rolling-origin comparisons use identical quarters for lag-1, seasonal lag-4, and the calibrated chip-delivery stock-flow baseline; full-window and recent-8 metrics are reported.",
      scenarioPolicy: "Scenario parameters transform future output only. Monitor estimates and historical rolling-origin points remain fixed at baseline assumptions.",
      frontierPolicy: "Frontier observations constrain single-site scale and commissioning cadence only; projected rows are excluded from historical actuals and frontier values are never globally summed.",
    },
    sources: [
      {
        id: "epoch-ai-data-center-power",
        label: "Epoch AI · AI Data Center Timelines",
        url: input.epochPower.source.url,
        role: "Observed site IT/facility power calibration and projected-site constraints",
        asOf: input.epochPower.quality.asOf,
      },
      {
        id: "epoch-frontier-data-centers",
        label: "Epoch AI · Frontier Data Centers",
        url: input.epochPower.source.frontierUrl,
        role: "Single-site upper-bound and commissioning-cadence constraint only",
        asOf: input.epochPower.quality.asOf,
      },
      {
        id: "epoch-ai-chip-sales",
        label: "Epoch AI · AI Chip Sales",
        url: "https://epoch.ai/data/ai-chip-sales",
        role: "Quarterly accelerator power deliveries and uncertainty input",
        asOf: input.compute.monitor.asOf,
      },
    ],
  };
}
